import {
  EllipsedText,
  EmptyState,
  FlexColumn,
  FlexRow,
  ImageThumbnail,
  Loading,
  MaterialIcon,
  useLoading,
  VideoThumbnail,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Cell, Grid } from 'baseui/layout-grid';
import { HeadingXSmall, LabelMedium, LabelSmall } from 'baseui/typography';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FILETYPE_PICTURE, FILETYPE_VIDEO } from '../../constants/app';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getS3FileUrlByKey } from '../../services/appService';
import { streamingCurrentStateState, streamingState } from '../../storage/streaming';
import { Event } from '../../types/gptTypes';
import { StreamingState } from '../../types/streaming';
import { getValidEventPositions } from '../../utils/gpt';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import MediaPreviewModal from '../shared/MediaPreviewModal';
import { StyledTooltip } from '../ui/StyledTooltip';
import { GptEventDetailData } from './GptEventDetailData';
import { useGptAttendEvent } from './hooks/useGptAttendEvent';
import { useGptEventDetail } from './hooks/useGptEventDetail';

export function GptEventDetail() {
  const { theme, css, classes, concatenateClasses } = useStyles();
  const { event, reloadEvent, loadingEvent } = useGptEventDetail();
  const { fitToLngLats } = useCamera();
  const { loading, startLoading, stopLoading } = useLoading();
  const { onAttend } = useGptAttendEvent();
  const { zoomTo } = useCamera();
  const [eventEvidences, setEventEvidences] = useState<Event>();
  const [initialZoom, setInitialZoom] = useState(false);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<number>();
  const [streaming, setStreaming] = useRecoilState(streamingState);
  const streamingCurrentState = useRecoilValue(streamingCurrentStateState);
  const isStreaming = !!streamingCurrentState;
  const resetStreaming = useResetRecoilState(streamingState);
  const frontIsOk = streaming.frontState === StreamingState.ok;
  const internalIsOk = streaming.internalState === StreamingState.ok;
  const domeIsOk = streaming.domeState === StreamingState.ok;
  const streamingIsActive =
    streamingCurrentState?.asset.channels === 2 ? frontIsOk || internalIsOk : frontIsOk || internalIsOk || domeIsOk;
  // TODO: Validate if we need to disable streaming using camera features as event.cs is shortened;
  // const enabledCameraFeatures = hasEnabledCameraFeatures(event?.cs);

  const evidenceFiles = useMemo(() => {
    return eventEvidences?.evidences;
  }, [eventEvidences]);

  useEffect(() => {
    const loadEvidencesUrl = async () => {
      if (event?.evidences.length) {
        startLoading();
        const newEvidences = [...event.evidences];
        const requests = event.evidences.map((evidence, index) => {
          return getS3FileUrlByKey(evidence.key, true).then((result) => ({ ...result, index }));
        });
        const results = await Promise.allSettled(requests);
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            const index = result.value.index;
            const url = result.value.url ?? '';
            const evidence = newEvidences[index];
            newEvidences[index] = {
              ...evidence,
              url,
            };
          }
        });
        const newEventEvidences = {
          ...event,
          evidences: newEvidences,
        };
        setEventEvidences(newEventEvidences);
        stopLoading();
      }
    };

    loadEvidencesUrl();
  }, [event]);

  useUpdateEffect(() => {
    if (event && !initialZoom) {
      setTimeout(() => {
        fitToLngLats(getValidEventPositions(event));
        setInitialZoom(true);
      }, 100);
    }
  }, [event, fitToLngLats]);

  useUpdateEffect(() => {
    setStreaming((prev) => ({
      ...prev,
      channels: streamingCurrentState?.asset.channels ?? 2,
    }));
  }, [streamingCurrentState?.asset.channels]);

  const onOpenStreaming = () => {
    setStreaming((prev) => ({
      ...prev,
      ident: event?.ident,
    }));
  };

  const onUpdate = () => {
    reloadEvent();
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      {!event && !loadingEvent && <EmptyState title="No se pudo cargar el evento." />}
      {!event && loadingEvent && <Loading />}
      {event && (
        <>
          <FlexRow gap={theme.sizing.scale300}>
            <FlexRow classNames={concatenateClasses(classes.centeredStart, css({ flex: 1 }))}>
              <FlexRow
                gap={theme.sizing.scale200}
                classNames={classes.centeredStart}
              >
                <HeadingXSmall margin={0}>
                  <EllipsedText
                    breakAll
                    inline
                  >
                    {event.ident}
                  </EllipsedText>
                </HeadingXSmall>
                <LabelMedium
                  margin={0}
                  color={theme.colors.contentSecondary}
                >
                  <EllipsedText
                    breakAll
                    inline
                  >
                    {`- ${event.eventName}`}
                  </EllipsedText>
                </LabelMedium>
              </FlexRow>
            </FlexRow>
            <FlexRow
              gap={theme.sizing.scale100}
              classNames={css({ alignItems: 'center' })}
            >
              <StyledTooltip
                content="Ubicación actual"
                showArrow={false}
              >
                <Button
                  shape="circle"
                  kind="tertiary"
                  size="compact"
                  onClick={() => zoomTo({ lat: event.cs.latitude, lng: event.cs.longitude })}
                >
                  <MaterialIcon
                    name="location_searching"
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
              <StyledTooltip
                content="Actualizar"
                showArrow={false}
              >
                <Button
                  size="compact"
                  shape="circle"
                  onClick={onUpdate}
                  kind="tertiary"
                  isLoading={loadingEvent}
                >
                  <MaterialIcon
                    name={'sync'}
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
              <StyledTooltip
                content="Atender"
                showArrow={false}
              >
                <Button
                  size="compact"
                  shape="circle"
                  onClick={onAttend}
                  kind="tertiary"
                >
                  <MaterialIcon
                    name={'assignment_late'}
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
              {!isStreaming && (
                <StyledTooltip
                  content="Ver en vivo"
                  showArrow={false}
                >
                  <Button
                    size="compact"
                    shape="circle"
                    onClick={onOpenStreaming}
                    kind="tertiary"
                  >
                    <MaterialIcon
                      name="live"
                      size="mini"
                    />
                  </Button>
                </StyledTooltip>
              )}
              {isStreaming && (
                <StyledTooltip
                  content="Cerrar en vivo"
                  showArrow={false}
                >
                  <Button
                    size="compact"
                    shape="circle"
                    onClick={resetStreaming}
                    kind="tertiary"
                    overrides={{
                      Root: {
                        style: {
                          position: 'relative',
                        },
                      },
                    }}
                  >
                    <MaterialIcon
                      name="live"
                      size="mini"
                      color={streamingIsActive ? theme.colors.negative : theme.colors.contentPrimary}
                    />
                    <span
                      style={{
                        width: '12px',
                        height: '12px',
                        background: theme.colors.contentPrimary,
                        position: 'absolute',
                        top: '5px',
                        right: '3px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <MaterialIcon
                        name="close"
                        color={theme.colors.contentInversePrimary}
                        size="8px"
                      />
                    </span>
                  </Button>
                </StyledTooltip>
              )}
            </FlexRow>
          </FlexRow>
          <FlexColumn gap={theme.sizing.scale300}>
            <GptEventDetailData event={event} />
          </FlexColumn>
          <FlexRow
            centered
            gap={theme.sizing.scale200}
            classNames={css({
              background: theme.colors.backgroundSecondary,
              padding: theme.sizing.scale200,
              borderRadius: theme.sizing.scale300,
              boxSizing: 'border-box',
            })}
          >
            <LabelSmall>
              <span style={{ color: theme.colors.contentSecondary }}>Estado:</span> {event.statusDisplay || '-'}
            </LabelSmall>
          </FlexRow>

          {loading && <Loading />}
          {!loading && !!eventEvidences?.evidences.length && (
            <FlexColumn
              classNames={css({
                overflowY: 'hidden',
                height: '100%',
              })}
            >
              <FlexRow
                gap={theme.sizing.scale200}
                classNames={classes.centeredStart}
              >
                <HeadingXSmall margin={0}>Evidencias</HeadingXSmall>
              </FlexRow>

              <FlexColumn
                classNames={css({ overflowY: 'auto' })}
                gap={0}
              >
                <Grid
                  gridColumns={12}
                  gridGaps={[8]}
                  gridGutters={[16]}
                  gridMargins={[0]}
                  behavior="fluid"
                  overrides={{
                    GridWrapper: {
                      style: {
                        width: '100%',
                        margin: 0,
                        overflowX: 'hidden',
                      },
                    },
                  }}
                >
                  {eventEvidences?.evidences.map((evidenceFile, index) => {
                    if (!evidenceFile.url) return null;

                    const imageIndex = evidenceFiles?.findIndex((file) => file.key === evidenceFile.key);
                    return (
                      <Cell
                        span={[12, 12, 12, 12, 6]}
                        key={index}
                      >
                        {evidenceFile.fileType === FILETYPE_PICTURE && (
                          <ImageThumbnail
                            url={evidenceFile.url}
                            onClick={() => setSelectedEvidenceIndex(imageIndex)}
                            imageStyle={{
                              width: '100%',
                              cursor: 'pointer',
                            }}
                          />
                        )}
                        {evidenceFile.fileType === FILETYPE_VIDEO && (
                          <VideoThumbnail
                            onClick={() => setSelectedEvidenceIndex(imageIndex)}
                            url={evidenceFile.url}
                            videoStyle={{
                              width: '100%',
                              cursor: 'pointer',
                            }}
                          />
                        )}
                      </Cell>
                    );
                  })}
                </Grid>
              </FlexColumn>
            </FlexColumn>
          )}
          {selectedEvidenceIndex !== undefined && evidenceFiles && (
            <MediaPreviewModal
              isOpen
              onClose={() => setSelectedEvidenceIndex(undefined)}
              mediaFiles={evidenceFiles}
              selectedItemIndex={selectedEvidenceIndex}
            />
          )}
        </>
      )}
    </FloatingCardContent>
  );
}
